import R from 'ramda';
import DateUtils from '../../../utils/date-utils';
import moment from 'moment';
import uuid from 'node-uuid';

const checkIfArrayVaryBySeason = (array) => {
  return array
    && array.length
    && (
    array.length > 1
    || R.path([0, 'dateRanges', 'length'], array) > 1
  );
};

export const composeForPolicyNotes = (dialog, draftContentInView) => {
  const { type: editType, policyType, data } = dialog;
  return R.call(R.cond([
    [R.equals('billingInfo'), () => {
      // billing-info-node.jsx
      const { billingInformation } = draftContentInView;
      return {
        ...billingInformation,
        insuranceFiles: billingInformation.insuranceFiles || [],
      };
    }],
    [R.equals('checkInOut'), () => {
      // checkinout.jsx
      const checkInOutInfo = R.pick(['checkIn',
        'checkOut',
        'earlyCheckIn',
        'earlyCheckInFee',
        'earlyCheckInCostType',
        'earlyCheckInPercentageType',
        'lateCheckOut',
        'lateCheckOutFee',
        'lateCheckOutCostType',
        'lateCheckOutPercentageType'], draftContentInView);
      return {
        ...checkInOutInfo,
        lateCheckOutChecked: Boolean(checkInOutInfo.lateCheckOut),
        earlyCheckInChecked: Boolean(checkInOutInfo.earlyCheckIn),
        earlyCheckInCostType: checkInOutInfo.earlyCheckInCostType || '$',
        lateCheckOutCostType: checkInOutInfo.lateCheckOutCostType || '$',
      };
    }],
    [R.equals('policy'), () => {
      const { locationPolicy } = draftContentInView;
      const getLocationPolicyEditInfo = () => {
        const [policyInfo] = (locationPolicy || []).filter(policy => policy.policyType === policyType);
        const { note } = (() => {
          switch (policyType) {
            case 'cancellation':
              return { note: 'Your cancellation policy tells your partners when, and how much, they will be charged for cancelling a booking.' };
            case 'group':
              return { note: 'Please note any special conditions or policies that apply for group bookings.' };
            case 'freeOfCharge':
              return { note: 'On occasion, your partners will have tour leaders, escorts, or guides accompany a group. Please note any conditions in which they will not be charged as part of the booking.' };
            case 'family':
              return { note: 'How many adults and children are included in your family rates?' };
            case 'child':
              return { note: 'What ages are included in your Child Policy?' };
            case 'infant':
              return { note: 'What ages are included in your Infant Policy?' };
            default:
              return {};
          }
        })();
        return {
          note,
          cancelHoursDays: 'hours',
          focChildrenAllowed: Boolean(policyInfo && policyInfo.focChildrenCount),
          policyType,
          ...R.omit(['applicable'], policyInfo),
        };
      };
      return getLocationPolicyEditInfo();
    }],
    [() => R.contains(editType, ['corporateIdentificationNumber', 'termsAndConditions', 'submissionCriteria', 'marketInfo']), () => {
      // terms-criteria-node.jsx
      const { forSupplier } = data;
      const info = draftContentInView[forSupplier ? 'supplierInfo' : 'buyerInfo'];
      return {
        fieldName: editType,
        value: info[editType],
      };
    }],
  ]), editType);
};

export default (dialog, contractActivitiesProducts) => {
  const { rightSideContent: draftContentInView, leftNav: metaDataForDraft, draftErrors } = contractActivitiesProducts;
  const {
    allContractingReadyProducts,
    productsArray,
    startDate,
    endDate,
    productSharedData: {
      allExtras,
      allOffers,
    } = {},
    contractId,
    locationId,
    supplierId,
  } = metaDataForDraft;
  const { type: editType, policyType, data, offersOrExtras } = dialog;
  const {
    _id: ratePackageId,
    productName,
    ratePackageName,
    seasons,
    seasonRates,
    productType,
  } = draftContentInView;
  const existingSeasons = ((seasonRates || []).length > (seasons || []).length ? seasonRates : seasons || []);
  const isSingleSeason = R.path([0, 'dateRanges', 0, 'seasonStartDate'], existingSeasons) === new Date(startDate).toISOString()
    && R.path([0, 'dateRanges', 0, 'seasonEndDate'], existingSeasons) === new Date(endDate).toISOString();

  const checkIfSeasonalSectionsIsIncomplete = (array) => {
    const varyBySeason = checkIfArrayVaryBySeason(array);
    return varyBySeason && array.length < existingSeasons.length;
  };
  return R.call(R.cond([
    [() => R.contains(editType, [
      'corporateIdentificationNumber',
      'termsAndConditions',
      'submissionCriteria',
      'marketInfo',
      'policy',
      'checkInOut',
      'billingInfo',
    ]), () => composeForPolicyNotes(dialog, draftContentInView)],
    [R.equals('addRates'), R.always({ })], // left-three-rates.jsx
    [R.equals('addProducts'), () => {
      // add-product-button.jsx
      return {
        products: [
          ...allContractingReadyProducts,
          {
            productName: '',
            added: false,
            checked: false,
            bedsAndSofas: { isApartment: false, rooms: [] },
          },
        ],
      };
    }],
    [R.equals('manageRatePackage'), () => {
      // rate-set-infor-template.jsx
      // three-dots-menuItems.jsx
      const existingPackagesForProduct = productsArray
        .filter(prod => prod.productId === data.productId)
        .map(prod => ({ ...prod, ratePackageName: prod.ratePackageName || 'Primary', oldRatePackageName: prod.ratePackageName || 'Primary' }));
      return {
        title: ratePackageName ? `Manage Rate Packages for ${productName}` : `Create Rate Package for ${productName}`,
        existingPackagesForProduct,
      };
    }],
    [R.equals('allocation'), () => {
      // right-one.jsx
      const { allocation } = draftContentInView;
      const allocationEmpty = !(allocation && allocation.length);
      if (allocationEmpty) {
        return {
          allocation: isSingleSeason ? existingSeasons.map(season => {
            return {
              allotments: [{}],
              releaseBackDays: '',
              dateRanges: season.dateRanges,
            };
          }) : [{
            allotments: [{}],
            releaseBackDays: '',
          }],
        };
      }
      const varyBySeason = checkIfArrayVaryBySeason(allocation);
      const incomplete = checkIfSeasonalSectionsIsIncomplete(allocation);
      return {
        title: 'Edit Allocation',
        varyBySeason,
        allocation: incomplete ? DateUtils.matchAllocationOrPolicyWithSeasons(existingSeasons, allocation) : allocation,
      };
    }],
    [R.equals('addProductPolicy'), () => {
      const { productPolicy } = draftContentInView;
      return {
        productPolicy,
        selectedPolicy: {},
      };
    }],
    [R.equals('editProductPolicy'), () => {
      const { productPolicy } = draftContentInView;
      const policyObject = (productPolicy || []).reduce((result, policy) => {
        if (policy.policyType === 'child') {
          result.child = { ...policy, focChildrenAllowed: Boolean(policy.focChildrenCount) };
        } else result[policy.policyType] = policy;
        return result;
      }, {});
      return {
        productPolicy,
        selectedPolicy: policyObject[policyType],
      };
    }],
    [R.equals('seasonalCancelPolicies'), () => {
      const { productPolicy } = draftContentInView;
      const [policyInfo] = (productPolicy || []).filter(policy => policy.policyType === 'cancellation');
      const contentLength = R.path(['cancellations', 'length'], policyInfo);
      if (contentLength) {
        const varyBySeason = checkIfArrayVaryBySeason(policyInfo.cancellations);
        const incomplete = checkIfSeasonalSectionsIsIncomplete(policyInfo.cancellations);
        return {
          varyBySeason,
          ...policyInfo,
          cancellations: incomplete ?
            DateUtils.matchAllocationOrPolicyWithSeasons(existingSeasons, policyInfo.cancellations) : policyInfo.cancellations,
        };
      }
      const varyBySeason = productType === 'Non-Accommodation' ? false : !isSingleSeason;
      return {
        varyBySeason,
        policyType: 'cancellation',
        conditions: '',
        cancellations: varyBySeason ? existingSeasons.map(season => ({ dateRanges: season.dateRanges })) : [{ dateRanges: [{ seasonStartDate: startDate, seasonEndDate: endDate }] }],
      };
    }],
    [R.equals('blackouts'), () => {
      const { blackouts } = draftContentInView;
      return {
        blackouts: blackouts ? [...blackouts.map((obj) => R.assoc('dataKey', uuid.v1(), obj)), { dataKey: uuid.v1() }] : [{}],
      };
    }],
    [R.equals('addExtra'), R.always({})],
    [R.equals('editExtra'), () => {
      // left-extras.jsx
      const { extras } = draftContentInView;
      return { extras };
    }],
    [R.equals('pickupDropoff'), () => {
      // right-four-pickup-dropoff.jsx
      const { pickupOrDropoff } = data;
      const infoArray = draftContentInView[pickupOrDropoff];
      if (infoArray && infoArray.length) {
        const useSeasons = infoArray.length > 1 && infoArray.length === existingSeasons.length;
        const varyBySeason = checkIfArrayVaryBySeason(infoArray);
        // const incomplete = checkIfSeasonalSectionsIsIncomplete(infoArray);
        return {
          varyBySeason: useSeasons || varyBySeason,
          useSeasons,
          [pickupOrDropoff]: useSeasons ?
            DateUtils.matchAllocationOrPolicyWithSeasons(existingSeasons, infoArray)
            : infoArray.map(obj => obj && R.isEmpty(obj.dateRanges) ? R.merge(obj, { dateRanges: [{}] }) : obj),
        };
      }
      return {
        varyBySeason: false,
        useSeasons: false,
        [pickupOrDropoff]: [{
          notes: '',
          timeLocArray: [{}],
          dateRanges: [{ seasonStartDate: startDate, seasonEndDate: endDate }],
        }],
      };
    }],
    [R.equals('importFromTemplate'), R.always({ step: 1 })],
    [R.equals('omniImport'), R.always({
      showCC: true,
      templates: data.templates || [],
      importPoliciesNotes: true,
      targetTemplate: {
        docId: contractId,
        locationId,
        supplierId,
        products: allContractingReadyProducts,
      },
    })],
    [R.equals('copyDataFromAnotherProduct'), () => {
      const { data: { noShowIfEmpty } } = dialog;
      const currentProductId = draftContentInView.productId;
      const currentRatePackage = draftContentInView.ratePackageName || '';
      const notEmptyRatePackages = (draftErrors.products || []).filter(prod => {
        const notEmpty = prod.productCompletionScore > 0;
        const notSameProductId = prod.productId !== currentProductId;
        const notSamePackageName = (prod.ratePackageName || '') !== currentRatePackage;
        return notEmpty && (notSameProductId || notSamePackageName);
      });
      const availableFields = productType === 'Accommodation' ?
        ['seasonRates', 'extras', 'offers', 'allocation', 'blackouts', 'productPolicy']
        : ['seasonRates', 'extras', 'offers', 'allocation', 'blackouts', 'pickup', 'dropoff', 'productPolicy'];
      return {
        addedProducts: notEmptyRatePackages,
        withoutRates: true,
        productSelectedToBeCopied: notEmptyRatePackages.length === 1 ? 0 : -1,
        availableFields: availableFields.map(field => ({ field, checked: true })),
        thisProductType: productType,
        noShowIfEmpty,
      };
    }],
    [R.equals('editSeason'), R.always({
      existingSeasons,
    })],
    [R.equals('manageExtrasOffers'), () => {
      const list = offersOrExtras.type === 'offers' ? allOffers.map(obj => R.merge(obj, {
        validDatesOfSale: (() => {
          if (obj.isStayPay) return [{ fromDate: new Date().toISOString(), toDate: endDate }];
          if (obj.validDatesOfSale.length) return obj.validDatesOfSale;
          return [{}];
        })(),
        validDatesOfTravel: obj.validDatesOfTravel.length ? obj.validDatesOfTravel : [{}],
      })) : allExtras;
      const retVal = {
        offersOrExtras: {
          selectedId: offersOrExtras.selectedId || R.path([0, '_id'], list),
          toDelete: offersOrExtras.toDelete ? ratePackageId : null,
          productsArray,
          productType,
          list,
        },
      };
      if (offersOrExtras.createNew) {
        if (offersOrExtras.type === 'offers') {
          retVal.data = {
            title: data.title,
            offerName: '',
            offerDetail: '',
            specialConditions: '',
            allowCombination: false,
            validDatesOfSale: [{ fromDate: moment(new Date()).isAfter(endDate) ? startDate : moment.utc().startOf('day').toISOString(), toDate: endDate }],
            validDatesOfTravel: [{}],
            ratePackages: productsArray.map(p => p._id),
          };
        } else {
          retVal.data = {
            title: data.title,
            ratePackages: productsArray.map(p => p._id),
          };
        }
      }
      if (offersOrExtras.toDelete) {
        retVal.offersOrExtras.list = retVal.offersOrExtras.list.map(o => {
          if (o._id === retVal.offersOrExtras.selectedId) return { ...o, ratePackages: o.ratePackages.filter(x => x !== ratePackageId) };
          return o;
        });
      }
      return retVal;
    }],
    [R.equals('updateBeddingConfig'), () => {
      const foundProd = allContractingReadyProducts.find(p => p.productId === draftContentInView.productId);
      return {
        title: 'Update Bedding Configuration',
        bedsAndSofas: foundProd ? foundProd.bedsAndSofas : { rooms: [[]], isApartment: false },
      };
    }],
  ]), editType);
};



// WEBPACK FOOTER //
// ./src/app/components/contracting/__storeFiles__/composeDraftEditDataByType.js